// @ts-check

/** @file Immutable project-tenant query sources for local replica screens. */

import {PROJECT_REPLICA_DATABASE_IDENTIFIER, projectReplicaIdentity} from "./project-replica-contract.js"

/** @import RecordModel from "velocious/build/src/database/record/index.js" */
/** @import ModelClassQuery from "velocious/build/src/database/query/model-class-query.js" */
/** @import {OpenProjectReplica} from "./project-replica.js" */
/** @typedef {typeof RecordModel} RecordModelClass */

/**
 * Builds a row source permanently bound to one captured project database.
 * @template {RecordModelClass} MC - Project-replica model class.
 * @param {{modelClass: MC, projectId: string, query?: (query: ModelClassQuery<MC>) => ModelClassQuery<MC> | void, replica: OpenProjectReplica}} args Source arguments.
 * @returns {{databaseIdentityForModel: (observedModelClass: RecordModelClass) => string, getModelClass: () => MC, toArray: () => Promise<InstanceType<MC>[]>}} Tenant-bound live-query source.
 */
export function projectReplicaRowsSource({modelClass, projectId, query, replica}) {
  assertProjectReplicaOwnership({modelClass, projectId, replica})

  return /** @type {{databaseIdentityForModel: (observedModelClass: RecordModelClass) => string, getModelClass: () => MC, toArray: () => Promise<InstanceType<MC>[]>}} */ (replica.handle.liveQuery({
    databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
    modelClass,
    query
  }))
}

/**
 * Builds a live count source that executes inside the same captured operation as its query.
 * @template {RecordModelClass} MC - Project-replica model class.
 * @param {{modelClass: MC, projectId: string, query?: (query: ModelClassQuery<MC>) => ModelClassQuery<MC> | void, replica: OpenProjectReplica}} args Source arguments.
 * @returns {{databaseIdentityForModel: (observedModelClass: RecordModelClass) => string, getModelClass: () => MC, toArray: () => Promise<number[]>}} Tenant-bound count source.
 */
export function projectReplicaCountSource({modelClass, projectId, query, replica}) {
  assertProjectReplicaOwnership({modelClass, projectId, replica})
  const rowsSource = replica.handle.liveQuery({
    databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER,
    modelClass,
    query: (baseQuery) => {
      const builtQuery = query ? query(baseQuery) || baseQuery : baseQuery
      const primaryKey = modelClass.primaryKey()

      return builtQuery.select(Array.isArray(primaryKey) ? primaryKey : [primaryKey])
    }
  })

  return Object.freeze({
    /**
     * Resolves the physical database observed for one model.
     * @param {RecordModelClass} observedModelClass Model class named by the observer.
     * @returns {string} Captured physical database identity.
     */
    databaseIdentityForModel(observedModelClass) {
      return rowsSource.databaseIdentityForModel(observedModelClass)
    },

    /**
     * Returns the model observed by React live-query subscriptions.
     * @returns {MC} Project-replica model class.
     */
    getModelClass() {
      return modelClass
    },

    /**
     * Executes the count inside the captured tenant database operation.
     * @returns {Promise<number[]>} Resolves with the tenant-bound live count.
     * @rejects {Error} When query ownership is mismatched or count execution fails.
     */
    async toArray() {
      return [(await rowsSource.toArray()).length]
    }
  })
}

/**
 * Fails closed unless the supplied replica and model own the requested project database.
 * @param {{modelClass: RecordModelClass, projectId: string, replica: OpenProjectReplica}} args Ownership inputs.
 * @returns {void} Nothing when replica ownership is valid.
 * @throws {Error} When the replica, handle, model, or readiness does not match.
 */
function assertProjectReplicaOwnership({modelClass, projectId, replica}) {
  const requestedIdentity = projectReplicaIdentity({projectId, userId: replica.identity.userId})

  if (replica.identity.projectId !== requestedIdentity.projectId) {
    throw new Error(`Active project replica does not own requested project ${requestedIdentity.projectId}`)
  }
  const handleIdentity = projectReplicaIdentity(replica.handle.tenant())
  if (handleIdentity.projectId !== requestedIdentity.projectId || handleIdentity.userId !== requestedIdentity.userId) {
    throw new Error("Active project replica handle identity does not match its lifecycle owner")
  }
  assertProjectReplicaModel({handle: replica.handle, modelClass})
  const lifecycle = replica.handle.inspect({databaseIdentifier: PROJECT_REPLICA_DATABASE_IDENTIFIER})
  if (!lifecycle.ready) throw new Error("Active project replica database is not ready")
}

/**
 * Verifies that an observed model belongs to the captured tenant database.
 * @param {{handle: OpenProjectReplica["handle"], modelClass: RecordModelClass}} args Model ownership inputs.
 * @returns {void} Nothing when model ownership is valid.
 * @throws {Error} When the model belongs to another physical database.
 */
function assertProjectReplicaModel({handle, modelClass}) {
  const databaseIdentifier = modelClass.getDatabaseIdentifier({tenant: handle.tenant()})

  if (databaseIdentifier !== PROJECT_REPLICA_DATABASE_IDENTIFIER) {
    throw new Error(`${modelClass.getModelName()} does not belong to the project replica database`)
  }
}
